import { useStudio } from "./StudioContext";
import { StudioPage, Panel, SectionLabel, Metric, Loading, Empty, cx } from "./StudioShared";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, PieChart, Pie } from "recharts";

const titleCase = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());
const GOLD = "#D4AF37", GREEN = "#10B981", SLATE = "#64748B";
const tip = { background: "#0A1128", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", fontSize: 12, borderRadius: 2 };

export default function Analytics() {
  const { analytics } = useStudio();
  if (!analytics) return <StudioPage title="Analytics" question="What is working?"><Loading /></StudioPage>;

  const mi = analytics.mission_intelligence || {};
  const strat = analytics.strategists || {};
  const prov = analytics.providers || {};
  const biz = analytics.business || {};

  const industryData = Object.entries(mi.plans_by_industry || {}).map(([name, value]) => ({ name: titleCase(name), value }));
  const confDist = Object.entries(mi.confidence_distribution || {}).map(([name, value]) => ({ name: name.replace(/\(.*\)/, "").trim(), value }));
  const stratData = Object.entries(strat.most_consulted || {}).slice(0, 10).map(([name, value]) => ({ name: name.replace(" Strategist", ""), value }));
  const provData = Object.entries(prov.usage || {}).map(([name, v]) => ({ name: titleCase(name), ...v }));
  const CONF_COLORS = { "high": GREEN, "medium": GOLD, "low": "#F97316" };
  const eventsEmpty = !biz.events || Object.keys(biz.events).length === 0;

  return (
    <StudioPage title="Analytics" question="What is working?" testid="analytics">
      {/* MI headline metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric testid="an-total-plans" label="Total Mission Plans" value={mi.total_mission_plans || 0} sub={`${mi.projects_with_mi || 0} projects`} />
        <Metric testid="an-avg-conf" label="Avg Confidence" value={mi.avg_confidence != null ? `${Math.round(mi.avg_confidence * 100)}%` : "—"} />
        <Metric testid="an-avg-strat" label="Avg Strategists / Plan" value={(mi.avg_strategists_consulted || 0).toFixed(1)} />
        <Metric testid="an-mi-rate" label="MI-Enhanced Proposals" value={`${Math.round((biz.mi_enhanced_proposal_rate || 0) * 100)}%`} sub={`${biz.proposals_mi_enhanced || 0}/${biz.proposals_total || 0}`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Plans by Industry" testid="chart-industry">
          {industryData.length === 0 ? <Empty text="No data yet." /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={industryData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#ffffff20" }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tip} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="value" radius={[0, 2, 2, 0]}>{industryData.map((_, i) => <Cell key={i} fill={GOLD} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Confidence Distribution" testid="chart-confidence">
          {confDist.every((d) => d.value === 0) ? <Empty text="No data yet." /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={confDist} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {confDist.map((d, i) => <Cell key={i} fill={CONF_COLORS[d.name] || SLATE} />)}
                </Pie>
                <Tooltip contentStyle={tip} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex justify-center gap-4 mt-2">
            {confDist.map((d) => <span key={d.name} className="flex items-center gap-1.5 text-xs text-slate-400"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: CONF_COLORS[d.name] || SLATE }} />{d.name} ({d.value})</span>)}
          </div>
        </ChartCard>
      </div>

      <ChartCard title="Strategist Usage" testid="chart-strategists" className="mb-6">
        {stratData.length === 0 ? <Empty text="No data yet." /> : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stratData} margin={{ left: -10 }}>
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={{ stroke: "#ffffff20" }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={{ stroke: "#ffffff20" }} allowDecimals={false} />
              <Tooltip contentStyle={tip} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>{stratData.map((_, i) => <Cell key={i} fill={GOLD} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Providers + Business */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <SectionLabel className="mb-2">Provider Usage</SectionLabel>
          <Panel className="p-4 space-y-3" data-testid="an-providers">
            {provData.length === 0 ? <Empty text="No provider calls recorded." /> : provData.map((p) => (
              <div key={p.name}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-white">{p.name}</span>
                  <span className="text-slate-500 text-xs font-mono">{p.calls} calls · {Math.round((p.success_rate || 0) * 100)}% ok · {p.avg_ms}ms</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-sm overflow-hidden"><div className="h-full" style={{ width: `${Math.round((p.success_rate || 0) * 100)}%`, background: GREEN }} /></div>
              </div>
            ))}
            <div className="text-xs text-slate-500 pt-1">Failover events: {prov.failover_events ?? 0}</div>
          </Panel>
        </div>
        <div>
          <SectionLabel className="mb-2">Business Impact</SectionLabel>
          <Panel className="p-4 space-y-3" data-testid="an-business">
            <Row label="Projects with MI" value={`${biz.projects_with_mi || 0} / ${biz.projects_total || 0}`} />
            <Row label="Recommendation acceptance" value={biz.recommendation_acceptance_rate != null ? `${Math.round(biz.recommendation_acceptance_rate * 100)}%` : "—"} />
            <Row label="Proposal conversion" value={biz.proposal_conversion_rate != null ? `${Math.round(biz.proposal_conversion_rate * 100)}%` : "—"} />
            <Row label="Proposals viewed / approved" value={`${biz.proposal_viewed || 0} / ${biz.proposal_approved || 0}`} />
            {eventsEmpty && <div className="text-xs text-slate-500 border-t border-white/5 pt-3 leading-relaxed">Forward-looking conversion metrics populate as live customers view roadmaps, accept recommendations, and approve proposals.</div>}
          </Panel>
        </div>
      </div>
    </StudioPage>
  );
}

const ChartCard = ({ title, children, className = "", testid }) => (
  <Panel className={cx("p-5", className)} data-testid={testid}>
    <SectionLabel className="mb-4">{title}</SectionLabel>
    {children}
  </Panel>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm"><span className="text-slate-400">{label}</span><span className="text-white font-mono">{value}</span></div>
);
